/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { PrimaryButton, Button } from 'ui';
import Icon from 'components/core/icon';

import { FluidPreloader } from 'components/core/preloader';

import s from './styles.module.scss';

interface Props {
  show?: boolean;
  title: React.ReactNode;
  content: React.ReactNode;
  isConfirmModal?: boolean;
  onClose: () => void;
  onConfirm?: (() => void) | null;
  closeButtonText?: string | null;
  confirmButtonText?: string | null;
  dataAttribute?: string | null;
  dataConfirmAttribute?: string | null;
  isSubmitting?: boolean;
  confirmDisabled?: boolean;
}

const Modal = ({
  show = false,
  title,
  content,
  isConfirmModal = false,
  onClose,
  onConfirm = null,
  closeButtonText = null,
  confirmButtonText = null,
  dataAttribute = null,
  dataConfirmAttribute = null,
  isSubmitting = false,
  confirmDisabled = false,
}: Props) => {
  const modalRef = React.createRef<HTMLDivElement>();
  const { t } = useTranslation();
  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target !== modalRef.current && !modalRef.current?.contains(e.target as Node)) {
      onClose();
    }
  };

  const handleConfirm = (e: React.MouseEvent) => {
    e.stopPropagation();
    onConfirm?.();
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  if (!show) {
    return null;
  }

  return createPortal(
    <div role="presentation" className={s.modalBack} onClick={handleOutsideClick} data-cy={dataAttribute ?? undefined}>
      <div className={s.modal} ref={modalRef} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <h2 className={s.modalHeaderTitle}>{title}</h2>
          {onClose && (
            <button type="button" className={s.modalHeaderClose} onClick={handleClose}>
              <Icon icon={Icon.icons.cross} className={s.icon} />
            </button>
          )}
        </div>
        <div className={s.modalBody}>{content}</div>
        <div className={s.modalFooter}>
          {isSubmitting ? (
            <FluidPreloader />
          ) : (
            <>
              {onClose && (
                <Button type="button" onClick={handleClose} className={s.modalButton}>
                  {closeButtonText || t('cancel')}
                </Button>
              )}
              {isConfirmModal && (
                <PrimaryButton
                  type="button"
                  onClick={handleConfirm}
                  className={s.modalButton}
                  data-cy={dataConfirmAttribute ?? undefined}
                  isDisabled={confirmDisabled}
                >
                  {confirmButtonText || t('ok')}
                </PrimaryButton>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.querySelector('#modal-root') as Element,
  );
};

export default Modal;
